import React from 'react';
import { Box, Switch, Typography } from '@mui/material';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import type { IntegrationSettings } from '../services/integrationApi';
import { SectionCard } from './ui';

export interface ShopifyStoreDeletesSectionProps {
  settings: IntegrationSettings;
  onChange: (partial: Partial<IntegrationSettings>) => void;
}

const T = 'integrations.storeDeletes';

/**
 * Products deleted in Shopify (flag `shopify_respect_store_deletes`): by default a listing the store
 * deleted stays deleted; this switch lets an Enrich or Override sync create it again, as it did
 * before the flag. Hidden with the flag off.
 */
const ShopifyStoreDeletesSection: React.FC<ShopifyStoreDeletesSectionProps> = ({ settings, onChange }) => {
  const { t } = useAppTranslation();
  const { enabled } = useOrgFeatureFlag('shopify_respect_store_deletes');
  if (!enabled) return null;
  const label = t(`${T}.recreate.label`);

  return (
    <SectionCard title={t(`${T}.title`)} subtitle={t(`${T}.subtitle`)} dense padded>
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, mr: 2, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {label}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
            {t(`${T}.recreate.desc`)}
          </Typography>
        </Box>
        <Switch
          size="small"
          checked={settings.recreateDeletedProducts === true}
          onChange={(e) => onChange({ recreateDeletedProducts: e.target.checked })}
          slotProps={{ input: { 'aria-label': label } }}
        />
      </Box>
    </SectionCard>
  );
};

export default ShopifyStoreDeletesSection;
